import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Icon, useToast } from '@repo/design-system';
import { ink, space, typeRole } from '@repo/design-tokens';
import type { FloorRemovalRecord } from '@repo/frontend-types';
import { countFloorContents, floorCountsLine } from '@repo/front-end-utils';
import type { PutBackOutcome } from './putBackFloor';
import { DeadEndMark } from './launchFlags';

/** A record's counts line, read off the floor it holds (R18). */
const countsOf = (record: FloorRemovalRecord) =>
  floorCountsLine(countFloorContents({ name: '', floors: [record.floor] }, 0));

/** What the toast adds when part of what the delete took could not go back (R19). */
const skippedDetail = ({ composites, observers }: { composites: number; observers: number }) =>
  [
    composites > 0
      ? 'Some composite parts could not be restored because their measure was deleted.'
      : null,
    observers > 0
      ? 'Some camera regions could not be restored because what they watched was deleted.'
      : null,
  ]
    .filter(Boolean)
    .join(' ');

/**
 * The Launch step's *Removed at Launch* list (`floor-plan-page-floors`, R18–R19; Rob's ruling of
 * 2026-10-13): each floor deleted from the live twin since the last launch, what it holds, and *Put
 * back*. Not drawn on a frame — built from the step's own block and design-system pieces under Rob's
 * best-effort ruling, for his page review.
 *
 * Mounted only while there is something on it, inside the wizard's `ToastProvider`. `focusAfterLast`
 * is where focus goes when the last floor is put back and this list unmounts under it.
 */
export function LaunchRemovedFloors({
  records,
  onPutBack,
  onRetrySave,
  focusAfterLast,
  launching = false,
}: {
  records: readonly FloorRemovalRecord[];
  /**
   * Go Live is running. Put back is held for the whole launch: activation builds from a snapshot taken
   * when it started, so a floor put back now would be saved as present while the live twin omits it.
   */
  launching?: boolean;
  onPutBack: (record: FloorRemovalRecord) => Promise<PutBackOutcome>;
  /** Save the plan again after a failed save (the floor is already back in the plan). */
  onRetrySave: () => Promise<{ saved: boolean; reason?: string | null }>;
  focusAfterLast?: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<number | null>(null);
  const buttons = useRef(new Map<number, HTMLButtonElement>());

  const saveFailed = (name: string, reason?: string | null) => {
    toast.show({
      tone: 'error',
      icon: <Icon name="triangle-exclamation" />,
      title: `${name} is back, but the plan could not be saved.`,
      inlineAction: {
        label: 'Retry',
        onAction: () => {
          void onRetrySave()
            .then(again => {
              if (!again.saved) saveFailed(name, again.reason);
            })
            .catch(() => saveFailed(name));
        },
      },
    });
    if (reason) console.warn('[floors] put back saved nothing:', reason);
  };

  const putBack = async (record: FloorRemovalRecord) => {
    if (launching) return;
    // Where focus goes once this row is gone: the next row, else the step's own summary.
    const i = records.indexOf(record);
    const next = records[i + 1] ?? records[i - 1];
    setBusy(record.floor.id);
    // Focus moves on only when the row goes; a refused or failed Put back leaves it where it is.
    let gone = false;
    try {
      const outcome = await onPutBack(record);
      if (outcome.blocked) {
        toast.show({
          tone: 'error',
          icon: <Icon name="triangle-exclamation" />,
          title: `${record.floor.name} could not be put back`,
          detail: `Something added since uses ${outcome.blocked.length === 1 ? 'one of its codes' : `${outcome.blocked.length} of its codes`} (${outcome.blocked.slice(0, 3).join(', ')}).`,
        });
        return;
      }
      const restored = outcome.restored;
      if (!restored) return;
      gone = true;
      const detail = skippedDetail(restored.skipped);
      toast.show({
        tone: 'success',
        icon: <Icon name="rotate-left" />,
        title: restored.renamedFrom
          ? `${restored.renamedFrom} is back on the plan as ${restored.restoredName}.`
          : `${restored.restoredName} is back on the plan.`,
        ...(detail ? { detail } : {}),
      });
      if (!outcome.saved) saveFailed(restored.restoredName, outcome.reason);
    } catch (error) {
      // A save that threw rather than answering: the floor may be back in the plan, unsaved.
      console.warn('[floors] put back failed:', error);
      saveFailed(record.floor.name);
    } finally {
      setBusy(null);
      if (gone) {
        setTimeout(() => {
          const target = next ? buttons.current.get(next.floor.id) : null;
          if (target) target.focus();
          else focusAfterLast?.();
        }, 0);
      }
    }
  };

  return (
    <div className="launch-block" data-removed-floors="">
      <div className="block-head">
        {/* A dead end: the floors cannot be put back once the twin is launched. */}
        <DeadEndMark>
          <Icon name="trash-can" />
        </DeadEndMark>
        <h4>Removed at Launch</h4>
      </div>
      <p>
        These floors come off the live twin when it is launched. Until then you can put them back.
      </p>
      <Rows>
        {records.map(record => (
          <li key={record.floor.id}>
            <span className="name">{record.floor.name}</span>
            <span className="counts">{countsOf(record)}</span>
            <Button
              ref={el => {
                if (el) buttons.current.set(record.floor.id, el);
                else buttons.current.delete(record.floor.id);
              }}
              type="button"
              variant="frosted"
              size="xs"
              aria-label={`Put back ${record.floor.name}`}
              iconLeft={<Icon name="rotate-left" />}
              // `aria-disabled`, not `disabled`: the pressed button keeps focus while it saves.
              aria-disabled={busy !== null || launching || undefined}
              onClick={() => {
                if (busy === null && !launching) void putBack(record);
              }}
            >
              Put back
            </Button>
          </li>
        ))}
      </Rows>
    </div>
  );
}

const Rows = styled.ul`
  list-style: none;
  margin: ${space[2]} 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${space[2]};

  li {
    display: flex;
    align-items: center;
    gap: ${space[3]};
  }

  .name {
    font-family: ${typeRole.body1Stronger.fontFamily};
    font-size: ${typeRole.body1Stronger.fontSize};
    line-height: ${typeRole.body1Stronger.lineHeight};
    font-weight: ${typeRole.body1Stronger.fontWeight};
    color: ${ink[1]};
  }

  .counts {
    font-family: ${typeRole.body1.fontFamily};
    font-size: ${typeRole.body1.fontSize};
    line-height: ${typeRole.body1.lineHeight};
    font-weight: ${typeRole.body1.fontWeight};
    color: ${ink[3]};
    flex: 1;
  }
`;
